/**
 * Copyright (c) 2018-2020 mol* contributors, licensed under MIT, See LICENSE file for more info.
 *
 * @author Alexander Rose <alexander.rose@weirdbyte.de>
 */
import { isEmptyLoci, isEveryLoci } from '../mol-model/loci';
import { applyMarkerAction } from '../mol-util/marker-action';
import { Mat4 } from '../mol-math/linear-algebra';
import { updateTransformData, fillIdentityTransform } from '../mol-geo/geometry/transform-data';
import { calculateTransformBoundingSphere } from '../mol-gl/renderable/util';
import { ValueCell } from '../mol-util';
import { createOverpaint, clearOverpaint, applyOverpaintColor } from '../mol-geo/geometry/overpaint-data';
import { Interval } from '../mol-data/int';
import { createTransparency, clearTransparency, applyTransparencyValue, getTransparencyAverage } from '../mol-geo/geometry/transparency-data';
import { createClipping, applyClippingGroups, clearClipping } from '../mol-geo/geometry/clipping-data';
export { Visual };
var Visual;

(function (Visual) {
  function setVisibility(renderObject, visible) {
    if (renderObject) renderObject.state.visible = visible;
  }

  Visual.setVisibility = setVisibility;

  function setAlphaFactor(renderObject, alphaFactor) {
    if (renderObject) renderObject.state.alphaFactor = alphaFactor;
  }

  Visual.setAlphaFactor = setAlphaFactor;

  function setPickable(renderObject, pickable) {
    if (renderObject) renderObject.state.pickable = pickable;
  }

  Visual.setPickable = setPickable;

  function setColorOnly(renderObject, colorOnly) {
    if (renderObject) renderObject.state.colorOnly = colorOnly;
  }

  Visual.setColorOnly = setColorOnly;

  function mark(renderObject, loci, action, lociApply) {
    if (!renderObject) return false;
    var _a = renderObject.values,
        tMarker = _a.tMarker,
        uGroupCount = _a.uGroupCount,
        instanceCount = _a.instanceCount;
    var count = uGroupCount.ref.value * instanceCount.ref.value;
    var array = tMarker.ref.value.array;
    var changed = false;

    if (isEveryLoci(loci)) {
      changed = applyMarkerAction(array, Interval.ofLength(count), action);
    } else if (!isEmptyLoci(loci)) {
      changed = lociApply(loci, function (interval) {
        return applyMarkerAction(array, interval, action);
      }, true);
    }

    if (changed) ValueCell.update(tMarker, tMarker.ref.value);
    return changed;
  }

  Visual.mark = mark;

  function setOverpaint(renderObject, overpaint, lociApply, clear) {
    if (!renderObject) return;
    var _a = renderObject.values,
        tOverpaint = _a.tOverpaint,
        uGroupCount = _a.uGroupCount,
        instanceCount = _a.instanceCount;
    var count = uGroupCount.ref.value * instanceCount.ref.value; // ensure texture has right size

    createOverpaint(overpaint.layers.length ? count : 0, renderObject.values);
    var array = tOverpaint.ref.value.array; // clear all if requested

    if (clear) clearOverpaint(array, 0, count);

    var _loop_1 = function (i, il) {
      var _b = overpaint.layers[i],
          loci = _b.loci,
          color = _b.color,
          clear_1 = _b.clear;

      var apply = function (interval) {
        var start = Interval.start(interval);
        var end = Interval.end(interval);
        return clear_1 ? clearOverpaint(array, start, end) : applyOverpaintColor(array, start, end, color);
      };

      lociApply(loci, apply, false);
    };

    for (var i = 0, il = overpaint.layers.length; i < il; ++i) {
      _loop_1(i, il);
    }

    ValueCell.update(tOverpaint, tOverpaint.ref.value);
  }

  Visual.setOverpaint = setOverpaint;

  function setTransparency(renderObject, transparency, lociApply, clear) {
    if (!renderObject) return;
    var _a = renderObject.values,
        tTransparency = _a.tTransparency,
        transparencyAverage = _a.transparencyAverage,
        uGroupCount = _a.uGroupCount,
        instanceCount = _a.instanceCount;
    var count = uGroupCount.ref.value * instanceCount.ref.value; // ensure texture has right size and variant

    createTransparency(transparency.layers.length ? count : 0, renderObject.values);
    var array = tTransparency.ref.value.array; // clear if requested

    if (clear) clearTransparency(array, 0, count);

    var _loop_2 = function (i, il) {
      var _b = transparency.layers[i],
          loci = _b.loci,
          value = _b.value;

      var apply = function (interval) {
        var start = Interval.start(interval);
        var end = Interval.end(interval);
        return applyTransparencyValue(array, start, end, value);
      };

      lociApply(loci, apply, false);
    };

    for (var i = 0, il = transparency.layers.length; i < il; ++i) {
      _loop_2(i, il);
    }

    ValueCell.update(tTransparency, tTransparency.ref.value);
    ValueCell.updateIfChanged(transparencyAverage, getTransparencyAverage(array, count));
  }

  Visual.setTransparency = setTransparency;

  function setClipping(renderObject, clipping, lociApply, clear) {
    if (!renderObject) return;
    var _a = renderObject.values,
        tClipping = _a.tClipping,
        uGroupCount = _a.uGroupCount,
        instanceCount = _a.instanceCount;
    var count = uGroupCount.ref.value * instanceCount.ref.value; // ensure texture has right size

    createClipping(clipping.layers.length ? count : 0, renderObject.values);
    var array = tClipping.ref.value.array; // clear if requested

    if (clear) clearClipping(array, 0, count);

    var _loop_3 = function (i, il) {
      var _b = clipping.layers[i],
          loci = _b.loci,
          groups = _b.groups;

      var apply = function (interval) {
        var start = Interval.start(interval);
        var end = Interval.end(interval);
        return applyClippingGroups(array, start, end, groups);
      };

      lociApply(loci, apply, false);
    };

    for (var i = 0, il = clipping.layers.length; i < il; ++i) {
      _loop_3(i, il);
    }

    ValueCell.update(tClipping, tClipping.ref.value);
  }

  Visual.setClipping = setClipping;

  function setTransform(renderObject, transform, instanceTransforms) {
    if (!renderObject || !transform && !instanceTransforms) return;
    var values = renderObject.values;

    if (transform) {
      Mat4.copy(values.matrix.ref.value, transform);
      ValueCell.update(values.matrix, values.matrix.ref.value);
    }

    if (instanceTransforms) {
      values.extraTransform.ref.value.set(instanceTransforms);
      ValueCell.update(values.extraTransform, values.extraTransform.ref.value);
    } else if (instanceTransforms === null) {
      fillIdentityTransform(values.extraTransform.ref.value, values.instanceCount.ref.value);
      ValueCell.update(values.extraTransform, values.extraTransform.ref.value);
    }

    updateTransformData(values);
    var boundingSphere = calculateTransformBoundingSphere(values.invariantBoundingSphere.ref.value, values.aTransform.ref.value, values.instanceCount.ref.value);
    ValueCell.update(values.boundingSphere, boundingSphere);
  }

  Visual.setTransform = setTransform;
})(Visual || (Visual = {}));